import React, { useState, useEffect } from 'react';
import WhatsAppConnection from '../components/whatsapp/WhatsAppConnection';
import GmailSettings from '../components/settings/GmailSettings';
import ShopifySettings from '../components/settings/ShopifySettings';
import CustomStoreSettings from '../components/settings/CustomStoreSettings';
import FacebookSettings from '../components/settings/FacebookSettings';
import GoogleSheetsLeadsSettings from '../components/settings/GoogleSheetsLeadsSettings';
import MetaAdsSettings from '../components/settings/MetaAdsSettings';
import WhatsAppAutoMessagesSettings from '../components/settings/WhatsAppAutoMessagesSettings';
import StorefrontSettings from '../components/settings/StorefrontSettings';
import GeneralShopSettings from '../components/settings/GeneralShopSettings';
import TelegramSettings from '../components/settings/TelegramSettings';
import DailyDeliveryNotificationsSettings from '../components/settings/DailyDeliveryNotificationsSettings';
import api from '../utils/api';
import { supabase } from '../utils/supabase';
import { Card, Form, message } from 'antd';
// Mise en page menu à gauche / zone à droite, partagée avec la page Profil
import SettingsLayout from '../components/common/SettingsLayout';
import { ShopOutlined, MessageOutlined, MailOutlined, SettingOutlined, ApiOutlined, FacebookOutlined, BellOutlined, GlobalOutlined, SendOutlined, NotificationOutlined } from '@ant-design/icons';
// Indicateur d'activation de l'intégration Shopify (REACT_APP_ENABLE_SHOPIFY)
import { SHOPIFY_ENABLED } from '../config/features';

const Settings = () => {
  // Le retour OAuth Meta doit afficher directement l'onglet qui confirme la connexion.
  const [ongletActif, setOngletActif] = useState(() => (
    new URLSearchParams(window.location.search).get('tab') === 'meta-ads' ? 'meta-ads' : 'whatsapp'
  ));
  const [config, setConfig] = useState({
    llm_provider: 'claude',
    api_key: '',
    prompt_template: '',
    is_active: true,
    response_parameters: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [shopifyForm] = Form.useForm();

  useEffect(() => {
    // fetchConfig();
    loadSettings();
  }, []);

  // const fetchConfig = async () => {
  //   try {
  //     const { data } = await api.get('/bot/config');
  //     setConfig(data || {
  //       llm_provider: 'claude',
  //       api_key: '',
  //       prompt_template: '',
  //       response_parameters: {}
  //     });
  //   } catch (error) {
  //     console.error('Error fetching config:', error);
  //     setError('Erreur lors du chargement de la configuration');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: settings, error } = await supabase
        .from('settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'shopify')
        .single();

      
      if (settings) {
        shopifyForm.setFieldsValue({
          shop_domain: settings.shop_domain,
          webhook_secret: settings.webhook_secret,
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const saveShopifySettings = async (values) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('settings')
        .upsert({
          user_id: user.id,
          type: 'shopify',
          shop_domain: values.shop_domain,
          webhook_secret: values.webhook_secret,
        }, {
          onConflict: 'user_id,type'
        });

      if (error) throw error;

      message.success('Paramètres Shopify sauvegardés');
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');

    try {
      await api.put('/bot/config', config);
      setSuccessMessage('Configuration sauvegardée avec succès');
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Erreur lors de la sauvegarde de la configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  /**
   * Sections de la page, affichées par SettingsLayout (menu à gauche, zone à
   * droite). Toutes sont `flush` : chaque composant apporte déjà ses cartes.
   */
  const sections = [
    {
      id: 'whatsapp', label: 'WhatsApp', icon: <MessageOutlined />, flush: true,
      description: 'Connexion de votre compte WhatsApp.',
      content: <WhatsAppConnection />,
    },
    // Section Shopify : affichée uniquement si l'intégration est activée dans l'environnement
    ...(SHOPIFY_ENABLED ? [{
      id: 'shopify', label: 'Shopify', icon: <ShopOutlined />, flush: true,
      description: 'Synchronisation des produits, commandes et clients Shopify.',
      content: <ShopifySettings />,
    }] : []),
    {
      id: 'ecommerce-api', label: 'API E-commerce', icon: <ApiOutlined />, flush: true,
      description: 'Connexion d’une boutique externe via API.',
      content: <CustomStoreSettings />,
    },
    {
      id: 'facebook', label: 'Facebook', icon: <FacebookOutlined />, flush: true,
      description: 'Page Facebook et import des leads vers Google Sheets.',
      content: (
        <>
          <FacebookSettings />
          <GoogleSheetsLeadsSettings />
        </>
      ),
    },
    // Meta Ads : connexion du compte publicitaire pour la création de campagnes
    {
      id: 'meta-ads', label: 'Meta Ads', icon: <NotificationOutlined />, flush: true,
      description: 'Compte publicitaire utilisé pour créer vos campagnes.',
      content: <MetaAdsSettings />,
    },
    {
      id: 'auto-messages', label: 'Messages auto', icon: <BellOutlined />, flush: true,
      description: 'Messages WhatsApp automatiques et récapitulatifs de livraison.',
      content: (
        <>
          <WhatsAppAutoMessagesSettings />
          <DailyDeliveryNotificationsSettings />
        </>
      ),
    },
    {
      id: 'storefront', label: 'Site vitrine', icon: <GlobalOutlined />, flush: true,
      description: 'Apparence et options de votre boutique en ligne.',
      content: <StorefrontSettings />,
    },
    // Telegram pour les notifications admin via Telegram Bot
    {
      id: 'telegram', label: 'Telegram', icon: <SendOutlined />, flush: true,
      description: 'Notifications administrateur via un bot Telegram.',
      content: <TelegramSettings />,
    },
    // Gmail désactivé : { id: 'gmail', label: 'Gmail', icon: <MailOutlined />, content: <GmailSettings /> }
    {
      id: 'general', label: 'Général', icon: <SettingOutlined />, flush: true,
      description: 'Nom, pays, devise et coordonnées de la boutique active.',
      content: <GeneralShopSettings />,
    },
  ];

  return (
    <SettingsLayout
      title="Configuration"
      description="Gérez vos connexions, les automatisations WhatsApp et les intégrations de votre boutique."
      sections={sections}
      activeId={ongletActif}
      onChange={setOngletActif}
    />
  );
};

export default Settings;
